<template>
  <a-modal
    :title="l('AuditTeam_ManageMembersTitle', teamData?.name || '')"
    :width="1000"
    :visible="visible"
    :confirmLoading="confirmLoading"
    :maskClosable="false"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :okText="l('Save')"
    :cancelText="l('Cancel')"
  >
    <a-spin :spinning="loading">
      <template v-if="teamData">
        <!-- 团队基本信息 -->
        <a-alert
          :message="l('AuditTeam_TeamSummary', teamData.name, teamData.leaderName || '-', displayMembers.length)"
          type="info"
          show-icon
          style="margin-bottom: 16px"
        />

        <!-- 操作区域 -->
        <div class="member-actions">
          <a-button
            type="primary"
            icon="plus"
            @click="openPersonnelSelection"
            :disabled="selectedCurrentMembers.length > 0"
          >
            {{l('AuditTeam_AddMembers')}}
          </a-button>
          <a-button
            type="danger"
            icon="delete"
            @click="handleRemoveMembers"
            :disabled="selectedCurrentMembers.length === 0"
          >
            {{l('AuditTeam_RemoveSelected', selectedCurrentMembers.length)}}
          </a-button>
          <a-button icon="reload" @click="loadTeamMembers">{{l('Refresh')}}</a-button>
        </div>

        <!-- 当前成员列表 -->
        <a-card :title="l('AuditTeam_CurrentTeamMembers')" size="small" :bordered="false">
          <a-table
            :columns="memberColumns"
            :data-source="displayMembers"
            :row-selection="{
              selectedRowKeys: selectedCurrentMemberKeys,
              onChange: onCurrentMembersSelectChange,
              getCheckboxProps: record => ({
                disabled: record.userId === teamData.leaderId // 不能移除团队负责人
              })
            }"
            :pagination="false"
            size="small"
            :row-key="record => record.id || ('pending-' + record.userId)"
            :locale="{ emptyText: l('AuditTeam_NoTeamMembers') }"
          >
            <!-- 用户名列 -->
            <span slot="userName" slot-scope="text, record">
              <div class="user-info">
                <a-avatar v-if="record.userName" :size="small" style="background-color: #1890ff;">
                  {{ record.userName.charAt(0).toUpperCase() }}
                </a-avatar>
                <span class="user-name">{{ record.userName || '-' }}</span>
                <span v-if="record.realName && record.realName !== record.userName" class="real-name">
                  ({{ record.realName }})
                </span>
                <a-tag v-if="record.userId === teamData.leaderId" color="gold" size="small">
                  {{l('AuditTeam_Leader')}}
                </a-tag>
                <a-tag v-if="record.pending" color="blue" size="small">
                  {{l('AuditTeam_Pending')}}
                </a-tag>
              </div>
            </span>

            <!-- 加入时间列 -->
            <span slot="creationTime" slot-scope="text">
              {{ text | moment }}
            </span>
          </a-table>
        </a-card>
      </template>
    </a-spin>

    </a-modal>
</template>

<script>
import AppComponentBase from '../../../shared/component-base/app-component-base.js'
import { AuditTeamServiceProxy } from '../../../shared/service-proxies'
import { UserServiceProxy } from '../../../shared/service-proxies'
import moment from 'moment'
import PersonnelSelection from "../Personnel-selection/Personnel-selection.vue"
import { ModalHelper } from '@/shared/helpers'

export default {
  mixins: [AppComponentBase],
  name: 'ManageMembers',
  data() {
    return {
      visible: false,
      loading: false,
      confirmLoading: false,
      teamData: null,

      // 当前团队成员
      currentMembers: [],
      // 本地暂存的变更:保存时一次性提交
      pendingAdd: [],
      pendingRemoveIds: [],
      selectedCurrentMembers: [],
      selectedCurrentMemberKeys: [],

      // 添加成员相关
      addMembersLoading: false,
      availableUsers: [],
      availableUsersLoading: false,

      // 成员表格列配置
      memberColumns: [
        {
          title: this.l('AuditTeam_UserInfo'),
          dataIndex: 'userName',
          key: 'userName',
          scopedSlots: { customRender: 'userName' },
          width: 250,
          ellipsis: true
        },
          {
          title: this.l('AuditTeam_JoinTime'),
          dataIndex: 'joinTime',
          key: 'joinTime',
          scopedSlots: { customRender: 'creationTime' },
          width: 150
        }
      ],

      // 服务代理
      serviceProxy: null,
      userServiceProxy: null
    }
  },
  computed: {
    // 过滤掉已经是团队成员的用户
    filteredAvailableUsers() {
      const currentMemberIds = this.currentMembers.map(member => member.userId)
      return this.availableUsers.filter(user => !currentMemberIds.includes(user.id))
    },
    // 展示列表 = 已有成员(去除待移除) + 待添加成员
    displayMembers() {
      const kept = (this.currentMembers || []).filter(m => !this.pendingRemoveIds.includes(m.userId))
      return kept.concat(this.pendingAdd)
    }
  },
  created() {
    this.serviceProxy = new AuditTeamServiceProxy(this.$apiUrl, this.$api)
    this.userServiceProxy = new UserServiceProxy(this.$apiUrl, this.$api)
  },
  methods: {
    // 显示模态框
    open(id, teamName) {
      this.visible = true
      this.loading = true
      this.teamData = { id, name: teamName }

      // 加载团队成员和可选用户
      Promise.all([
        this.loadTeamMembers(),
        this.loadAvailableUsers()
      ]).finally(() => {
        this.loading = false
      })
    },

    // 加载团队成员
    loadTeamMembers() {
      return this.serviceProxy.getTeamMembers(this.teamData.id)
        .then(members => {
          this.currentMembers = members || []
          this.selectedCurrentMembers = []
          this.selectedCurrentMemberKeys = []
        })
        .catch(error => {
          console.error('获取团队成员失败:', error)
          this.$message.error(this.l('AuditTeam_GetMembersFailed') + (error.message || error))
        })
    },

    // 加载可选用户
    loadAvailableUsers() {
      this.availableUsersLoading = true
      return this.userServiceProxy.getLoginUserPage(false, null, null, 1000, 0)
        .then(result => {
          this.availableUsers = (result.items || []).map(user => ({
            id: user.id,
            name: user.realName || user.name,
            userName: user.userName || user.domainAccount || '',
            jobNumber: user.jobNumber || ''
          }))
        })
        .catch(error => {
          console.error('获取用户列表失败:', error)
          this.$message.error(this.l('AuditTeam_GetUserListFailed') + (error.message || error))
        })
        .finally(() => {
          this.availableUsersLoading = false
        })
    },

    // 当前团队成员选择变化
    onCurrentMembersSelectChange(selectedRowKeys, selectedRows) {
      this.selectedCurrentMemberKeys = selectedRowKeys
      this.selectedCurrentMembers = selectedRows.filter(row =>
        row.userId !== this.teamData.leaderId // 排除团队负责人
      )
    },

  
    // 打开人员选择组件
    openPersonnelSelection() {
      // 过滤掉已经是团队成员的用户
      const excludeUserIds = this.currentMembers.map(member => member.userId)

      ModalHelper.create(PersonnelSelection, {
        respId: excludeUserIds,
        isSelect: false,
        userType: 2
      }, {
        width: 1000
      }).subscribe(res => {
        if (res && res.length > 0) {
          // 过滤掉已经是团队成员的用户
          const availableNewMembers = res.filter(user =>
            !excludeUserIds.includes(user.userId)
          )

          if (availableNewMembers.length > 0) {
            // 本地暂存,保存时一次性提交(选人即时展示,不调接口)
            availableNewMembers.forEach(user => {
              this.pendingAdd.push({
                userId: user.userId,
                userName: user.userName || user.name,
                realName: user.realName || user.name,
                creationTime: null,
                pending: true
              })
            })
          } else {
            this.$message.warning(this.l('AuditTeam_AlreadyMember'))
          }
        }
      })
    },


    
  
    // 移除成员
    handleRemoveMembers() {
      if (this.selectedCurrentMembers.length === 0) {
        this.$message.warning(this.l('AuditTeam_PleaseSelectToRemove'))
        return
      }

      // 本地移除:待添加的直接撤销,已有成员记入暂存,保存时一次性提交
      this.selectedCurrentMembers.forEach(member => {
        const pendingIdx = this.pendingAdd.findIndex(m => m.userId === member.userId)
        if (pendingIdx >= 0) {
          this.pendingAdd.splice(pendingIdx, 1)
        } else {
          this.pendingRemoveIds.push(member.userId)
        }
      })
      this.selectedCurrentMembers = []
      this.selectedCurrentMemberKeys = []
    },

    // 检查用户是否已经是成员
    isUserAlreadyMember(userId) {
      return this.currentMembers.some(member => member.userId === userId)
    },

    // 用户搜索过滤
    filterOption(input, option) {
      const user = this.availableUsers.find(u => u.id === option.value)
      if (!user) return false

      const searchText = input.toLowerCase()
      return (
        user.name.toLowerCase().includes(searchText) ||
        user.userName.toLowerCase().includes(searchText) ||
        (user.jobNumber && user.jobNumber.toLowerCase().includes(searchText))
      )
    },

    // 提交保存
    handleSubmit() {
      // 一次性提交本地暂存的变更(添加+移除),无变更直接关闭
      if (this.pendingAdd.length === 0 && this.pendingRemoveIds.length === 0) {
        this.handleCancel()
        return
      }

      this.confirmLoading = true
      const addIds = this.pendingAdd.map(m => m.userId)
      const removeIds = [...this.pendingRemoveIds]
      const tasks = []
      if (addIds.length > 0) {
        tasks.push(this.serviceProxy.addMembers(this.teamData.id, addIds))
      }
      if (removeIds.length > 0) {
        tasks.push(this.serviceProxy.removeMembers(this.teamData.id, removeIds))
      }

      Promise.all(tasks)
        .then(() => {
          if (addIds.length > 0) this.$message.success(this.l('AuditTeam_AddMembersSuccess', addIds.length))
          if (removeIds.length > 0) this.$message.success(this.l('AuditTeam_RemoveMembersSuccess', removeIds.length))
          this.$emit('refresh') // 通知主页面刷新
          this.handleCancel()
        })
        .catch(error => {
          this.$message.error(this.l('AuditTeam_UpdateFailed') + (error.message || error))
        })
        .finally(() => {
          this.confirmLoading = false
        })
    },

    // 取消操作
    handleCancel() {
      this.visible = false
      this.teamData = null
      this.currentMembers = []
      this.selectedCurrentMembers = []
      this.selectedCurrentMemberKeys = []
      this.pendingAdd = []
      this.pendingRemoveIds = []
    }
  },
  filters: {
    moment(date) {
      return moment(date).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style lang="less" scoped>
.member-actions {
  margin-bottom: 16px;

  .ant-btn {
    margin-right: 8px;
  }
}

.user-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .user-name {
    font-weight: 500;
    color: #1890ff;
  }

  .real-name {
    color: #666;
    font-size: 12px;
  }
}

.user-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .job-number {
    color: #999;
    font-size: 12px;
    margin-left: 8px;
  }
}

.form-help {
  color: #999;
  font-size: 12px;
  margin-top: 8px;

  .anticon {
    margin-right: 4px;
  }
}

// 覆盖ant-design的样式
:deep(.ant-table-thead > tr > th) {
  background-color: #fafafa;
  font-weight: 600;
  color: #666;
}

:deep(.ant-card-head-title) {
  font-weight: 600;
  color: #1890ff;
}

:deep(.ant-select-disabled .ant-select-selection) {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
</style>